import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import InputField from '../../inputField';
import ActionButton from '../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import CustomDatePicker from '../../customDatePicker';
import {
  CURRENT_MONTH,
  CURRENT_YEAR,
  MONTH_FORMAT,
  YEAR_FORMAT,
} from '../../../../constants/dateRangeWithText';
import { isEmpty } from '../../../../utility/validation';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'minMax(0, 1fr) minMax(0, 1fr)',
    gridColumnGap: 8,
    gridTemplateAreas: `
        "firstText secondText"
        "startDate endDate"
      `,
  },
  firstText: {
    gridArea: 'firstText',
    minHeight: 90,
  },
  secondText: {
    gridArea: 'secondText',
  },
  startDate: {
    gridArea: 'startDate',
  },
  endDate: {
    gridArea: 'endDate',
  },
  headerItem: {
    marginBottom: spacing(2),
  },
  icon: {
    cursor: 'pointer',
    margin: spacing(0, 1, 0, 1),
  },
  field: {
    position: 'relative',
    marginBottom: spacing(6),
  },
  hasError: {
    marginBottom: spacing(1),
  },
  errorMessage: {
    position: 'absolute',
    bottom: -20,
  },
  fieldWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    flexShrink: 0,
  },
  group: {
    display: 'flex',
    alignItems: 'baseline',
  },
  yearPicker: {
    marginRight: spacing(1),
  },
  monthPicker: {
    marginLeft: spacing(1),
  },
}));

const DateRangeWithTextField = ({
  translations,
  id,
  value,
  fieldTypes,
  error = null,
  onDelete,
  onChange,
  isDisabled,
  ...rest
}) => {
  const classes = useStyles();
  const [
    yearFromField,
    monthFromField,
    yearToField,
    monthToField,
    firstTextField,
    secondTextField,
  ] = fieldTypes;

  const handleChangeText = useCallback(
    (e, name) => {
      onChange({ ...value, [name]: e.target.value }, id);
    },
    [id, value, onChange]
  );

  const handleChangeDate = useCallback(
    (name, newDate = null) => {
      onChange(
        {
          ...value,
          [name]: newDate,
        },
        id
      );
    },
    [id, value, onChange]
  );

  const getStartYearValue = () => {
    const month = isEmpty(value?.[monthFromField.name])
      ? value[monthFromField.name] - 1
      : 0;
    return new Date(value?.[yearFromField.name], month, 1);
  };

  const getStartMonth = () => {
    if (!isEmpty(value?.[monthFromField.name])) return null;
    const year =
      value?.[yearFromField.name] || value?.[yearToField.name] || CURRENT_YEAR;
    const month = value?.[monthFromField.name]
      ? value[monthFromField.name] - 1
      : 0;
    return new Date(year, month, 1);
  };

  const getEndYearValue = () => {
    if (value?.[yearToField.name] === CURRENT_YEAR) {
      return new Date(value?.[yearToField.name], CURRENT_MONTH, 20);
    }
    return value?.[yearToField.name]
      ? new Date(value?.[yearToField.name], 11, 20)
      : null;
  };

  const getEndMonth = () => {
    if (!isEmpty(value?.[monthToField.name])) return null;
    const year =
      value?.[yearToField.name] || value?.[yearFromField.name] || CURRENT_YEAR;
    const month = value?.[monthToField.name] ? value[monthToField.name] - 1 : 0;
    return new Date(year, month, 20);
  };

  return (
    <div className={classes.fieldWrapper}>
      <div className={classes.root}>
        <div className={classNames(classes.startDate)}>
          <FormHelperText
            classes={{
              root: classes.headerItem,
            }}
          >
            {translations.headers.startYear}
          </FormHelperText>
          <div className={classes.group}>
            <div
              key={yearFromField.name}
              className={classNames(classes.field, classes.group, {
                [classes.hasError]: !!error?.[yearFromField.name],
              })}
            >
              <div
                className={classNames(classes.fieldWrapper, classes.yearPicker)}
              >
                <CustomDatePicker
                  placeholder={
                    translations.field.labels[yearFromField.translationKey]
                      .placeholder
                  }
                  views={['year']}
                  value={getStartYearValue()}
                  disableToolbar
                  format={YEAR_FORMAT}
                  onChange={val => {
                    const yearValue = val?.getFullYear();
                    handleChangeDate(yearFromField.name, yearValue);
                  }}
                  isIconOnStart
                  error={!!error?.[yearFromField.name]}
                  errorMessage={
                    translations.field.validationMessages?.[
                      error?.[yearFromField.name]
                    ]
                  }
                  minDate={
                    yearFromField.getMinDate
                      ? yearFromField.getMinDate(value)
                      : undefined
                  }
                  maxDate={
                    yearFromField.getMaxDate
                      ? yearFromField.getMaxDate(value)
                      : undefined
                  }
                  disabled={isDisabled}
                  fullWidth
                  autoOk
                  {...rest}
                />
              </div>
            </div>
            <div
              key={monthFromField.name}
              className={classNames(classes.field, classes.group, {
                [classes.hasError]: !!error?.[monthFromField.name],
              })}
            >
              <div
                className={classNames(
                  classes.fieldWrapper,
                  classes.monthPicker
                )}
              >
                <CustomDatePicker
                  placeholder={
                    translations.field.labels[monthFromField.translationKey]
                      .placeholder
                  }
                  views={['month']}
                  value={getStartMonth()}
                  disableToolbar
                  format={MONTH_FORMAT}
                  onChange={val => {
                    const selectedMonth = isEmpty(val?.getMonth())
                      ? val.getMonth() + 1
                      : null;
                    handleChangeDate(monthFromField.name, selectedMonth);
                  }}
                  isIconOnStart
                  hasDeleteButton
                  onDelete={() => handleChangeDate(monthFromField.name)}
                  error={!!error?.[monthFromField.name]}
                  errorMessage={
                    translations.field.validationMessages?.[
                      error?.[monthFromField.name]
                    ]
                  }
                  fullWidth
                  minDate={
                    monthFromField.getMinDate
                      ? monthFromField.getMinDate(value)
                      : undefined
                  }
                  maxDate={
                    monthFromField.getMaxDate
                      ? monthFromField.getMaxDate(value)
                      : undefined
                  }
                  disabled={isDisabled}
                  autoOk
                  {...rest}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={classNames(classes.endDate)}>
          <FormHelperText
            classes={{
              root: classes.headerItem,
            }}
          >
            {translations.headers.endYear}
          </FormHelperText>
          <div className={classes.group}>
            <div
              key={yearToField.name}
              className={classNames(classes.field, classes.group, {
                [classes.hasError]: !!error?.[yearToField.name],
              })}
            >
              <div
                className={classNames(classes.fieldWrapper, classes.yearPicker)}
              >
                <CustomDatePicker
                  placeholder={
                    translations.field.labels[yearToField.translationKey]
                      .placeholder
                  }
                  views={['year']}
                  value={getEndYearValue()}
                  disableToolbar
                  format={YEAR_FORMAT}
                  onChange={val => {
                    const yearValue = val ? val.getFullYear() : val;

                    handleChangeDate(yearToField.name, yearValue);
                  }}
                  isIconOnStart
                  hasDeleteButton
                  onDelete={() => handleChangeDate(yearToField.name)}
                  error={!!error?.[yearToField.name]}
                  errorMessage={
                    translations.field.validationMessages?.[
                      error?.[yearToField.name]
                    ]
                  }
                  fullWidth
                  minDate={
                    yearToField.getMinDate
                      ? yearToField.getMinDate(value)
                      : undefined
                  }
                  maxDate={
                    yearToField.getMaxDate
                      ? yearToField.getMaxDate(value)
                      : undefined
                  }
                  disabled={isDisabled}
                  autoOk
                  {...rest}
                />
              </div>
            </div>
            <div
              key={monthToField.name}
              className={classNames(classes.field, classes.group, {
                [classes.hasError]: !!error?.[monthToField.name],
              })}
            >
              <div
                className={classNames(
                  classes.fieldWrapper,
                  classes.monthPicker
                )}
              >
                <CustomDatePicker
                  placeholder={
                    translations.field.labels[monthToField.translationKey]
                      .placeholder
                  }
                  views={['month']}
                  value={getEndMonth()}
                  disableToolbar
                  format={MONTH_FORMAT}
                  onChange={val => {
                    const selectedMonth = isEmpty(val?.getMonth())
                      ? val.getMonth() + 1
                      : null;
                    handleChangeDate(monthToField.name, selectedMonth);
                  }}
                  isIconOnStart
                  hasDeleteButton
                  onDelete={() => handleChangeDate(monthToField.name)}
                  error={!!error?.[monthToField.name]}
                  errorMessage={
                    translations.field.validationMessages?.[
                      error?.[monthToField.name]
                    ]
                  }
                  fullWidth
                  minDate={
                    monthToField.getMinDate
                      ? monthToField.getMinDate(value)
                      : undefined
                  }
                  maxDate={
                    monthToField.getMaxDate
                      ? monthToField.getMaxDate(value)
                      : undefined
                  }
                  disabled={isDisabled}
                  autoOk
                  {...rest}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={classNames(classes.firstText)}>
          <FormHelperText
            classes={{
              root: classes.headerItem,
            }}
          >
            {translations.headers.firstTextField}
          </FormHelperText>
          <div
            key={firstTextField.name}
            className={classNames(classes.field, {
              [classes.hasError]: !!error?.[firstTextField.name],
            })}
          >
            <div className={classes.fieldWrapper}>
              <InputField
                customErrorClass={classes.errorMessage}
                id={firstTextField.name}
                name={firstTextField.name}
                type={firstTextField.type}
                placeholder={
                  translations.field.labels[firstTextField.translationKey]
                    .placeholder
                }
                required={firstTextField.required}
                value={value?.[firstTextField.name] || ''}
                error={!!error?.[firstTextField.name]}
                errorMessage={
                  translations.field.validationMessages?.[
                    error?.[firstTextField.name]
                  ]
                }
                disabled={isDisabled}
                onChange={e => handleChangeText(e, firstTextField.name)}
                fullWidth
              />
            </div>
          </div>
        </div>
        <div className={classNames(classes.secondText)}>
          <FormHelperText
            classes={{
              root: classes.headerItem,
            }}
          >
            {translations.headers.secondTextField}
          </FormHelperText>
          <div
            key={secondTextField.name}
            className={classNames(classes.field, {
              [classes.hasError]: !!error?.[secondTextField.name],
            })}
          >
            <div className={classes.fieldWrapper}>
              <InputField
                customErrorClass={classes.errorMessage}
                id={secondTextField.name}
                name={secondTextField.name}
                type={secondTextField.type}
                placeholder={
                  translations.field.labels[secondTextField.translationKey]
                    .placeholder
                }
                required={secondTextField.required}
                value={value?.[secondTextField.name] || ''}
                error={!!error?.[secondTextField.name]}
                errorMessage={
                  translations.field.validationMessages?.[
                    error?.[secondTextField.name]
                  ]
                }
                disabled={isDisabled}
                onChange={e => handleChangeText(e, secondTextField.name)}
                fullWidth
              />
              <ActionButton
                className={classes.button}
                isDisabled={isDisabled}
                type={ACTION_BUTTON_TYPES.DELETE_FILL}
                onClickHandler={onDelete}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DateRangeWithTextField.propTypes = {
  translations: PropTypes.object.isRequired,
  id: PropTypes.number.isRequired,
  value: PropTypes.object.isRequired,
  fieldTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  error: PropTypes.object,
  onDelete: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DateRangeWithTextField;
